/** facet 한 개의 선택지. dropdown/select는 `count` 배지를, switcher는 label만 쓴다. */
export type FilterOption = {
  value: string;
  label: string;
  description?: string;
  count?: number;
  disabled?: boolean;
  group?: { value: string; label: string };
};

export type FilterGroup = {
  value: string;
  label: string;
  filters: readonly FilterOption[];
  disabled?: boolean;
  /** switcher/select의 선택 해제 항목 라벨. 기본값은 "All". */
  allLabel?: string;
  // 렌더 형태. "dropdown"(기본)=다중선택 드롭다운, "switcher"=단일선택
  // ContentSwitcher("All" 세그먼트=해제), "select"=단일선택 컴팩트 드롭다운("All" 옵션=해제).
  // switcher/select 모두 상호배타적 단일축 facet용 — select는 좁은 컨테이너(switcher가 wrap될 때)용.
  display?: "dropdown" | "switcher" | "select";
};

/** group.value → 선택된 filter.value 목록. */
export type FilterSelection = Readonly<Record<string, readonly string[]>>;

export type FilterControllerSnapshot = {
  groups: readonly FilterGroup[];
  selection: FilterSelection;
  selectedGroupCount: number;
  selectedFilterCount: number;
  hasSelection: boolean;
};

export type FilterSelectionChangeHandler = (
  selection: FilterSelection,
  snapshot: FilterControllerSnapshot,
) => void | Promise<void>;

export type FilterControllerOptions = {
  groups?: readonly FilterGroup[];
  initialSelection?: FilterSelection;
  onSelectionChange?: FilterSelectionChangeHandler;
};

const emptySelection: FilterSelection = Object.freeze({});

/**
 * facet groups와 선택 상태를 소유하는 외부 스토어(useSyncExternalStore 계약). `Filter`는 이
 * controller를 필수 prop으로 받아 구독·렌더만 한다. 선택 변경은 `onSelectionChange`로 통지하고,
 * 스냅샷은 변경 시에만 새 객체로 교체해 구독 안정성을 보장한다. (RFC-filter-search-primitives)
 */
export class FilterController {
  private readonly listeners = new Set<() => void>();
  // selection 변경 구독자. 생성자 onSelectionChange와 <Filter>의 onSelectionChange prop이
  // 모두 여기에 등록돼 함께 통지된다.
  private readonly selectionListeners = new Set<FilterSelectionChangeHandler>();
  private snapshot: FilterControllerSnapshot;
  private disposed = false;
  // 마지막으로 받은 groups 입력 reference. 외부 소유자가 같은 reference로 갱신을 반복해도
  // 불필요한 emit/렌더를 만들지 않는다.
  private lastGroups: readonly FilterGroup[];
  // 소비 앱이 생성 시 정한 기본 선택. clearAll(완전 해제)과 reset(서비스 기본 복원)을 구분한다.
  private readonly initialSelection: FilterSelection;

  constructor({
    groups = [],
    initialSelection = emptySelection,
    onSelectionChange,
  }: FilterControllerOptions = {}) {
    if (onSelectionChange) {
      this.selectionListeners.add(onSelectionChange);
    }
    this.initialSelection = copySelection(initialSelection);
    this.lastGroups = groups;
    this.snapshot = createSnapshot(groups, normalizeSelection(groups, this.initialSelection));
  }

  getSnapshot = (): FilterControllerSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.disposed = false;
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // selection 변경 구독. 생성자 onSelectionChange와 별개로 추가 소비자(예: <Filter>의
  // onSelectionChange prop)가 통지를 받게 한다. unsubscribe 함수를 반환.
  subscribeSelectionChange = (listener: FilterSelectionChangeHandler): (() => void) => {
    this.selectionListeners.add(listener);
    return () => {
      this.selectionListeners.delete(listener);
    };
  };

  setGroups(groups: readonly FilterGroup[]): void {
    if (groups === this.lastGroups) {
      return;
    }
    this.lastGroups = groups;
    const nextSelection = normalizeSelection(groups, this.snapshot.selection);
    const selectionChanged = !isSelectionEqual(this.snapshot.selection, nextSelection);
    this.setSnapshot(createSnapshot(groups, nextSelection));

    if (selectionChanged) {
      this.notifySelectionChange();
    }
  }

  replaceSelection(selection: FilterSelection): void {
    this.commitSelection(normalizeSelection(this.snapshot.groups, selection));
  }

  setGroupSelection(groupValue: string, filterValues: readonly string[]): void {
    this.commitSelection(
      normalizeSelection(this.snapshot.groups, {
        ...this.snapshot.selection,
        [groupValue]: filterValues,
      }),
    );
  }

  setFilterSelected(groupValue: string, filterValue: string, selected: boolean): void {
    if (!isSelectableFilter(this.snapshot.groups, groupValue, filterValue)) {
      return;
    }

    // 단일선택(switcher/select): select=값 교체, deselect=현재값이면 해제. 기존 값에 add하면 정규화가
    // 첫 filter-순서 값을 남겨 "새 값 선택"이 무시된다 — 다중선택 add/delete 시맨틱을 단일축에 쓰면 안 된다.
    const group = this.snapshot.groups.find((currentGroup) => currentGroup.value === groupValue);
    if (group && isSingleSelectGroup(group)) {
      if (selected) {
        this.setGroupSelection(groupValue, [filterValue]);
      } else if ((this.snapshot.selection[groupValue] ?? []).includes(filterValue)) {
        this.setGroupSelection(groupValue, []);
      }
      return;
    }

    const currentValues = this.snapshot.selection[groupValue] ?? [];
    const currentValueSet = new Set(currentValues);
    if (selected) {
      currentValueSet.add(filterValue);
    } else {
      currentValueSet.delete(filterValue);
    }

    this.setGroupSelection(groupValue, Array.from(currentValueSet));
  }

  toggleFilter(groupValue: string, filterValue: string): void {
    const currentValues = this.snapshot.selection[groupValue] ?? [];
    this.setFilterSelected(groupValue, filterValue, !currentValues.includes(filterValue));
  }

  clearGroup(groupValue: string): void {
    if (!this.snapshot.selection[groupValue]) {
      return;
    }

    const nextSelection: Record<string, readonly string[]> = {
      ...this.snapshot.selection,
    };
    delete nextSelection[groupValue];
    this.commitSelection(normalizeSelection(this.snapshot.groups, nextSelection));
  }

  clearAll = (): void => {
    this.commitSelection(emptySelection);
  };

  reset = (): void => {
    this.commitSelection(normalizeSelection(this.snapshot.groups, this.initialSelection));
  };

  dispose(): void {
    this.disposed = true;
    this.listeners.clear();
    this.selectionListeners.clear();
  }

  private commitSelection(selection: FilterSelection): void {
    if (isSelectionEqual(this.snapshot.selection, selection)) {
      return;
    }

    this.setSnapshot(createSnapshot(this.snapshot.groups, selection));
    this.notifySelectionChange();
  }

  private notifySelectionChange(): void {
    for (const listener of this.selectionListeners) {
      void Promise.resolve(listener(this.snapshot.selection, this.snapshot));
    }
  }

  private setSnapshot(snapshot: FilterControllerSnapshot): void {
    this.snapshot = snapshot;
    this.emit();
  }

  private emit(): void {
    if (this.disposed) {
      return;
    }

    for (const listener of this.listeners) {
      listener();
    }
  }
}

function copySelection(selection: FilterSelection): FilterSelection {
  return Object.fromEntries(
    Object.entries(selection).map(([groupValue, filterValues]) => [
      groupValue,
      Array.from(filterValues),
    ]),
  );
}

function createSnapshot(
  groups: readonly FilterGroup[],
  selection: FilterSelection,
): FilterControllerSnapshot {
  const selectedGroupCount = Object.keys(selection).length;
  const selectedFilterCount = Object.values(selection).reduce(
    (total, values) => total + values.length,
    0,
  );

  return {
    groups: Array.from(groups),
    selection,
    selectedGroupCount,
    selectedFilterCount,
    hasSelection: selectedFilterCount > 0,
  };
}

function normalizeSelection(
  groups: readonly FilterGroup[],
  selection: FilterSelection,
): FilterSelection {
  const nextSelection: Record<string, readonly string[]> = {};

  for (const group of groups) {
    if (group.disabled) {
      continue;
    }

    const selectedValueSet = new Set(selection[group.value] ?? []);
    const values = group.filters
      .filter((filter) => !filter.disabled && selectedValueSet.has(filter.value))
      .map((filter) => filter.value);

    // 단일선택 표시(switcher/select)는 값이 최대 1개. 컨트롤러가 여기서 collapse해 selection/count/필터링이
    // UI(첫 값만 표시)와 어긋나지 않게 한다 — 외부에서 replaceSelection/setFilterSelected로 여러 값이 들어와도
    // 첫 filter-순서 값만 남긴다(렌더러가 이미 보여주던 값과 동일). 다중선택(dropdown)은 그대로 둔다.
    const normalizedValues = isSingleSelectGroup(group) ? values.slice(0, 1) : values;

    if (normalizedValues.length > 0) {
      nextSelection[group.value] = normalizedValues;
    }
  }

  return Object.keys(nextSelection).length === 0 ? emptySelection : nextSelection;
}

// switcher/select는 상호배타적 단일선택 축 — 값이 최대 1개. dropdown(기본)은 다중선택.
function isSingleSelectGroup(group: FilterGroup): boolean {
  return group.display === "switcher" || group.display === "select";
}

function isSelectableFilter(
  groups: readonly FilterGroup[],
  groupValue: string,
  filterValue: string,
): boolean {
  const group = groups.find((currentGroup) => currentGroup.value === groupValue);
  if (!group || group.disabled) {
    return false;
  }

  return group.filters.some((filter) => filter.value === filterValue && !filter.disabled);
}

function isSelectionEqual(left: FilterSelection, right: FilterSelection): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => {
    const leftValues = left[key] ?? [];
    const rightValues = right[key] ?? [];
    return (
      rightKeys.includes(key) &&
      leftValues.length === rightValues.length &&
      leftValues.every((value, index) => value === rightValues[index])
    );
  });
}
